import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Grid,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Support as SupportIcon,
  Chat,
  PriorityHigh,
  Assignment,
  Reply,
  Close,
  CheckCircle,
  Schedule,
  Warning,
} from '@mui/icons-material';
import axios from 'axios';

interface SupportTicket {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  subject: string;
  description: string;
  category: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  createdAt: string;
  updatedAt: string;
  assignedTo?: string;
  messages: Array<{
    id: string;
    sender: string;
    message: string;
    timestamp: string;
    isStaff: boolean;
  }>;
}

const Support: React.FC = () => {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');

  useEffect(() => {
    fetchTickets();
  }, [statusFilter, priorityFilter]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (priorityFilter !== 'all') params.priority = priorityFilter;
      
      const response = await axios.get('/api/admin/support', { params });
      setTickets(response.data.tickets || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleViewTicket = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    setOpenDialog(true);
  };

  const handleUpdateStatus = async (ticketId: string, newStatus: string) => {
    try {
      await axios.put(`/api/admin/support/${ticketId}`, { status: newStatus });
      fetchTickets();
      if (selectedTicket && selectedTicket.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status: newStatus as any });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update ticket status');
    }
  };

  const handleSendReply = async () => {
    if (!selectedTicket || !replyMessage.trim()) return;

    try {
      await axios.post(`/api/admin/support/${selectedTicket.id}/reply`, {
        message: replyMessage,
      });

      const newMessage = {
        id: Date.now().toString(),
        sender: 'Admin',
        message: replyMessage,
        timestamp: new Date().toISOString(),
        isStaff: true,
      };

      setSelectedTicket({
        ...selectedTicket,
        messages: [...selectedTicket.messages, newMessage],
      });

      setReplyMessage('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send reply');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      case 'urgent': return 'error';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'warning';
      case 'in_progress': return 'info';
      case 'resolved': return 'success';
      case 'closed': return 'default';
      default: return 'default';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent': return <Warning />;
      case 'high': return <PriorityHigh />;
      default: return <Assignment />;
    }
  };

  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#FFD700' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
          Support Center
        </Typography>
        <Typography variant="body1" sx={{ color: '#888' }}>
          Manage customer support tickets and inquiries
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: '#FFD700', width: 48, height: 48 }}>
                  <SupportIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#888' }}>
                    Total Tickets
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: '#FF9800', width: 48, height: 48 }}>
                  <Schedule />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {stats.open}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#888' }}>
                    Open Tickets
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: '#2196F3', width: 48, height: 48 }}>
                  <Chat />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {stats.inProgress}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#888' }}>
                    In Progress
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: '#4CAF50', width: 48, height: 48 }}>
                  <CheckCircle />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {stats.resolved}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#888' }}>
                    Resolved
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel sx={{ color: '#888' }}>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status"
            sx={{
              color: 'white',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
            }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel sx={{ color: '#888' }}>Priority</InputLabel>
          <Select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            label="Priority"
            sx={{
              color: 'white',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
            }}
          >
            <MenuItem value="all">All Priority</MenuItem>
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="urgent">Urgent</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Tickets Table */}
      <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <TableContainer component={Paper} sx={{ bgcolor: 'transparent' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: '#888', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  Ticket ID
                </TableCell>
                <TableCell sx={{ color: '#888', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  Subject
                </TableCell>
                <TableCell sx={{ color: '#888', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  User
                </TableCell>
                <TableCell sx={{ color: '#888', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  Priority
                </TableCell>
                <TableCell sx={{ color: '#888', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  Status
                </TableCell>
                <TableCell sx={{ color: '#888', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  Created
                </TableCell>
                <TableCell align="right" sx={{ color: '#888', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map((ticket) => (
                <TableRow key={ticket.id}>
                  <TableCell sx={{ color: 'white', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    #{ticket.id.slice(-6)}
                  </TableCell>
                  <TableCell sx={{ color: 'white', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    <Box sx={{ maxWidth: 200 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {ticket.subject}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        {ticket.category}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: '#CCC', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    {ticket.userName}
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    <Chip
                      icon={getPriorityIcon(ticket.priority)}
                      label={ticket.priority}
                      size="small"
                      color={getPriorityColor(ticket.priority)}
                      sx={{ fontWeight: 'bold' }}
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    <Chip
                      label={ticket.status}
                      size="small"
                      color={getStatusColor(ticket.status)}
                      sx={{ fontWeight: 'bold' }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: '#CCC', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    {new Date(ticket.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right" sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    <IconButton
                      size="small"
                      sx={{ color: '#4ECDC4' }}
                      onClick={() => handleViewTicket(ticket)}
                    >
                      <Chat />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Ticket Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#1A1A1A',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle sx={{ color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Ticket #{selectedTicket?.id.slice(-6)} - {selectedTicket?.subject}
          </Typography>
          <IconButton onClick={() => setOpenDialog(false)} sx={{ color: '#888' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedTicket && (
            <Box>
              {/* Ticket Info */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#888' }}>User</Typography>
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    {selectedTicket.userName} ({selectedTicket.userEmail})
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#888' }}>Category</Typography>
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    {selectedTicket.category}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#888' }}>Priority</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      icon={getPriorityIcon(selectedTicket.priority)}
                      label={selectedTicket.priority}
                      size="small"
                      color={getPriorityColor(selectedTicket.priority)}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#888' }}>Status</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={selectedTicket.status}
                      size="small"
                      color={getStatusColor(selectedTicket.status)}
                    />
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', mb: 2 }} />

              {/* Description */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>
                  Description
                </Typography>
                <Typography variant="body2" sx={{ color: 'white' }}>
                  {selectedTicket.description}
                </Typography>
              </Box>

              {/* Messages */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ color: '#888', mb: 2 }}>
                  Conversation
                </Typography>
                <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {selectedTicket.messages.map((message) => (
                    <Box
                      key={message.id}
                      sx={{
                        mb: 2,
                        p: 2,
                        bgcolor: message.isStaff ? 'rgba(255, 215, 0, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                        borderRadius: 2,
                        borderLeft: `3px solid ${message.isStaff ? '#FFD700' : '#4ECDC4'}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" sx={{ color: '#FFD700', fontWeight: 'bold' }}>
                          {message.sender}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#888' }}>
                          {new Date(message.timestamp).toLocaleString()}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: 'white' }}>
                        {message.message}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Reply Box */}
              <Box>
                <TextField
                  label="Reply to ticket"
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                  }}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<Reply />}
                    onClick={handleSendReply}
                    disabled={!replyMessage.trim()}
                    sx={{
                      bgcolor: '#FFD700',
                      color: '#0A0A0A',
                      fontWeight: 'bold',
                      '&:hover': { bgcolor: '#E6C200' },
                    }}
                  >
                    Send Reply
                  </Button>
                  {selectedTicket.status === 'open' && (
                    <Button
                      variant="outlined"
                      onClick={() => handleUpdateStatus(selectedTicket.id, 'in_progress')}
                      sx={{ color: '#4ECDC4', borderColor: '#4ECDC4' }}
                    >
                      Mark In Progress
                    </Button>
                  )}
                  {(selectedTicket.status === 'open' || selectedTicket.status === 'in_progress') && (
                    <Button
                      variant="outlined"
                      onClick={() => handleUpdateStatus(selectedTicket.id, 'resolved')}
                      sx={{ color: '#4CAF50', borderColor: '#4CAF50' }}
                    >
                      Mark Resolved
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Support;